"use client";

import React, { useState, useEffect } from "react";
import { Search, Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { ProductsSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-4 w-16 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative py-2 text-sm font-default font-medium tracking-wide text-foreground transition-colors hover:text-secondary uppercase">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-secondary transition-all duration-300 ease-out group-hover:w-full" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-12">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-12">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-default font-semibold tracking-tight text-foreground hover:text-secondary uppercase border-b border-border/10 pb-4 transition-colors">
          {item.label}
        </Link>)}
    </div>;
}
function ExpandableSearch() {
  const [search, setSearch] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  return <div className={cn("relative flex items-center transition-all duration-500 ease-in-out", isExpanded ? "w-48 md:w-64" : "w-9")}>
      
      <div className={cn("w-full overflow-hidden transition-all duration-500", isExpanded ? "opacity-100 translate-x-0" : "opacity-0 pointer-events-none translate-x-4")}>
        <ProductsSearch value={search} onChange={setSearch} className="w-full [&_input]:pl-10 [&_input]:h-10 [&_input]:rounded-md [&_input]:bg-muted [&_input]:border-border [&_input]:text-sm [&_input]:font-default focus-within:[&_input]:border-secondary focus-within:[&_input]:ring-1 focus-within:[&_input]:ring-secondary transition-all" placeholder="Etsi varastoa..." />
      </div>
    </div>;
}
export default function VoltMainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section data-section-id="253080" id="header" className={cn("sticky top-0 z-50 w-full bg-background text-foreground transition-all duration-500 border-b", isScrolled ? "bg-background/80 backdrop-blur-md border-border/50 shadow-md" : "bg-background border-transparent")}>
      <header className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0 flex items-center">
          <Link to="/Etusivu" className="Etusivulinkki">
            <WvcLogo className="h-8 w-auto transition-transform duration-300 group-hover:scale-105 text-primary [&_svg]:h-8 [&_svg]:w-auto [&_svg]:transition-transform [&_svg]:duration-300 [&_svg]:group-hover:scale-105 [&_svg]:text-primary [&_svg]:rounded-none" />
          </Link>
        </div>

        {}
        <div className="flex-1 flex justify-center px-8">
          <MenuProvider menu_id="16">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="flex-shrink-0 flex items-center gap-2 md:gap-4">
          <ExpandableSearch />
          
          <div className="relative group">
            <Button asChild variant="default" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-default font-semibold rounded-md shadow-lg transition-all duration-300 hover:scale-105">
              <Link to="/Varaa">Tarkista varaustilanne</Link>
            </Button>
          </div>

          {}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-secondary">
                <Menu className="size-6" />
                <span className="sr-only">Avaa valikko</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background/95 backdrop-blur-xl border-l-border">
              <SheetTitle className="sr-only">Navigointi</SheetTitle>
              <div className="flex flex-col h-full pt-8">
                <MenuProvider menu_id="16">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </section>;
}